import { useState } from 'react';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { payoutAPI } from '../lib/api';
import { toast } from 'sonner';

export default function BulkPayoutExport() {
  const [period, setPeriod] = useState('week');
  const [busy, setBusy] = useState(null); // 'csv' | 'pdf' | null

  const download = async (format) => {
    setBusy(format);
    try {
      const res = await payoutAPI.adminBulkExport(format, period);
      const blob = new Blob([res.data], { type: format === 'csv' ? 'text/csv' : 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `localmove_payouts_${period}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(`Export failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
      data-testid="bulk-payout-export-card"
    >
      <div className="flex items-start sm:items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-orange-500 text-white flex items-center justify-center shrink-0">
          <Download className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-semibold text-zinc-900">Bulk Driver Payouts</h3>
          <p className="text-xs sm:text-sm text-zinc-600">
            Download payouts (jobs + tips) for all drivers in one file.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[140px] h-9 bg-white" data-testid="bulk-export-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Today</SelectItem>
            <SelectItem value="week">Last 7 Days</SelectItem>
            <SelectItem value="month">Last 30 Days</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => download('csv')}
          disabled={busy !== null}
          data-testid="bulk-export-csv-btn"
        >
          {busy === 'csv' ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <FileSpreadsheet className="w-4 h-4 mr-1" />
          )}
          CSV
        </Button>
        <Button
          size="sm"
          className="bg-orange-500 hover:bg-orange-600 text-white"
          onClick={() => download('pdf')}
          disabled={busy !== null}
          data-testid="bulk-export-pdf-btn"
        >
          {busy === 'pdf' ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <FileText className="w-4 h-4 mr-1" />
          )}
          PDF
        </Button>
      </div>
    </div>
  );
}
